import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined';
import WorkOutlinedIcon from '@mui/icons-material/WorkOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { withSnackbar } from '../../utils/SnackbarHOC';
import { AppButton } from '../../components/UI';
import { ErrorBanner, IconBox, Panel, PanelHeader } from './components/ui';
import { ImportCandidatesView, QueueView, RejectionsView, StoredCandidatesView } from './components/CandidateViews';
import { ImportMissionsView, MissionOperationsView, MissionsView } from './components/MissionViews';
import MatchingView from './components/MatchingView';
import LinkedinAccount from './components/LinkedinAccount';
import { LinkedinContext, useBrowserLinkedin } from './extensionBridge';
import { fetchDashboard, fetchResults, subscribeEvents, uploadCsv } from './api';
import { CANDIDATE_STEPS, MISSION_STEPS } from './constants';

const SECTIONS = [
  { key: 'dashboard', label: 'Tableau de bord', icon: <DashboardOutlinedIcon /> },
  {
    key: 'candidates', label: 'Candidats', icon: <PeopleOutlineIcon />,
    tabs: [
      { key: 'import', label: 'Importer' },
      { key: 'stored', label: 'Candidats sourcés' },
      { key: 'queue', label: 'File de relance' },
      { key: 'rejected', label: 'Non stockables' },
    ],
  },
  {
    key: 'missions', label: 'Missions', icon: <WorkOutlinedIcon />,
    tabs: [
      { key: 'import', label: 'Importer' },
      { key: 'stored', label: 'Missions sourcées' },
      { key: 'queue', label: 'File de relance' },
      { key: 'rejected', label: 'Non stockables' },
    ],
  },
  {
    key: 'matching', label: 'Matching', icon: <CompareArrowsIcon />,
    tabs: [
      { key: 'candidate-missions', label: 'Candidat → missions' },
      { key: 'mission-candidates', label: 'Mission → candidats' },
    ],
  },
];

const idleOp = (steps) => ({ phase: 'idle', fileName: '', jobId: null, steps, stepStates: {}, progress: {}, logs: [], error: '', results: [], summary: '' });

/** Runs a CSV import and follows its server-sent events; survives tab changes. */
function useImportOperation(steps, onFinished) {
  const [op, setOp] = useState(() => idleOp(steps));
  const unsubRef = useRef(null);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  useEffect(() => () => unsubRef.current?.(), []);

  const log = (line) => `[${new Date().toLocaleTimeString('fr-FR')}] ${line}`;

  const start = useCallback(async (file, mode, limit) => {
    setOp({ ...idleOp(steps), phase: 'running', fileName: file.name });
    try {
      const { job_id: jobId } = await uploadCsv(file, mode, limit);
      setOp((s) => ({ ...s, jobId, logs: [log(`Opération ${jobId} lancée`)] }));
      unsubRef.current?.();
      unsubRef.current = subscribeEvents(jobId, async (event) => {
        if (event.type === 'step_start') {
          setOp((s) => ({ ...s, stepStates: { ...s.stepStates, [event.step]: 'running' }, logs: [...s.logs, log(event.label)] }));
        } else if (event.type === 'progress') {
          setOp((s) => ({ ...s, progress: { ...s.progress, [event.step]: event } }));
        } else if (event.type === 'step_done') {
          setOp((s) => ({ ...s, stepStates: { ...s.stepStates, [event.step]: 'done' } }));
        } else if (event.type === 'error') {
          setOp((s) => ({ ...s, error: event.message, logs: [...s.logs, log(`Erreur : ${event.message}`)] }));
        } else if (event.type === 'close') {
          let data = {};
          try { data = await fetchResults(jobId); } catch (e) { /* keep empty */ }
          const results = data.results || [];
          const count = (st) => results.filter((r) => r.status === st).length;
          const failedRun = data.status !== 'done';
          setOp((s) => ({
            ...s,
            phase: failedRun ? 'error' : 'done',
            // The step that was running when the operation stopped is the one that failed.
            stepStates: failedRun
              ? Object.fromEntries(Object.entries(s.stepStates).map(([k, v]) => [k, v === 'running' ? 'error' : v]))
              : s.stepStates,
            results,
            error: data.status === 'done' ? s.error : data.error || s.error || 'La collecte LinkedIn a été interrompue.',
            summary: data.status === 'done' && results.length
              ? `${count('stored')} stocké(s), ${count('not_storable')} non stockable(s), ${count('extraction_failed') + count('storage_failed')} en échec.`
              : '',
          }));
          unsubRef.current = null;
          finishedRef.current?.();
        }
      });
    } catch (e) {
      setOp((s) => ({ ...s, phase: 'error', error: e.message }));
    }
  }, [steps]);

  const reset = useCallback(() => setOp(idleOp(steps)), [steps]);
  return { op, start, reset };
}

const KPI = [
  { key: 'stored', label: 'Profils stockés', hint: 'Prêts pour le matching', icon: <StorageOutlinedIcon />, tone: 'green', go: ['candidates', 'stored'] },
  { key: 'ready_for_storage', label: 'Prêts à relancer', hint: 'Stockage à reprendre', icon: <ScheduleIcon />, tone: 'blue', go: ['candidates', 'queue'] },
  { key: 'not_storable', label: 'Non stockables', hint: 'Refusés par le contrôle', icon: <ShieldOutlinedIcon />, tone: 'amber', go: ['candidates', 'rejected'] },
  { key: 'extraction_failed', label: "Échecs d'extraction", hint: 'Collecte à relancer', icon: <ErrorOutlinedIcon />, tone: 'red', go: ['candidates', 'rejected'] },
];

const FLOW = [
  { icon: <CloudUploadOutlinedIcon />, title: 'Import CSV', text: 'Une liste d’URL LinkedIn (profils ou offres).' },
  { icon: <TravelExploreIcon />, title: 'Collecte LinkedIn', text: 'Chaque page est lue avec des délais de sécurité.' },
  { icon: <FactCheckOutlinedIcon />, title: 'Contrôle factuel', text: 'Les valeurs sont vérifiées contre la page source.' },
  { icon: <AutoAwesomeOutlinedIcon />, title: 'Vectorisation', text: 'Stockage isolé, prêt pour le matching IA.' },
];

const DashboardView = ({ stats, error, loading, onRefresh, onGo }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <ErrorBanner>{error}</ErrorBanner>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
      {KPI.map((k) => (
        <Box
          key={k.key}
          role="button"
          tabIndex={0}
          onClick={() => onGo(...k.go)}
          onKeyDown={(e) => e.key === 'Enter' && onGo(...k.go)}
          sx={{ p: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', cursor: 'pointer', transition: 'all .15s', '&:hover': { borderColor: '#bcd6ee', boxShadow: '0 6px 18px rgba(3,64,123,0.08)', transform: 'translateY(-1px)' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <IconBox size={36} tone={k.tone}>{k.icon}</IconBox>
            {loading && <CircularProgress size={14} sx={{ color: '#cbd5e1' }} />}
          </Box>
          <Typography sx={{ mt: 1.5, fontSize: '1.7rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{stats?.[k.key] ?? '—'}</Typography>
          <Typography sx={{ mt: 0.5, fontSize: '0.84rem', fontWeight: 700, color: '#334155' }}>{k.label}</Typography>
          <Typography sx={{ fontSize: '0.74rem', color: '#94a3b8' }}>{k.hint}</Typography>
        </Box>
      ))}
    </Box>
    <Panel>
      <PanelHeader
        icon={<AutoAwesomeOutlinedIcon />}
        title="Comment fonctionne le sourcing"
        subtitle="Les profils et offres sourcés restent dans un espace isolé jusqu'à leur promotion vers le référentiel officiel."
        action={<AppButton size="small" variant="secondary" startIcon={<RefreshIcon />} onClick={onRefresh}>Actualiser</AppButton>}
      />
      <Box sx={{ p: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
        {FLOW.map((f, i) => (
          <Box key={f.title} sx={{ p: 1.75, borderRadius: '12px', bgcolor: '#f8fbfe', border: '1px solid #e2edf7' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconBox size={30}>{f.icon}</IconBox>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8' }}>ÉTAPE {i + 1}</Typography>
            </Box>
            <Typography sx={{ mt: 1, fontSize: '0.88rem', fontWeight: 800, color: '#0f172a' }}>{f.title}</Typography>
            <Typography sx={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.5 }}>{f.text}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <AppButton startIcon={<PeopleOutlineIcon />} onClick={() => onGo('candidates', 'import')}>Importer des candidats</AppButton>
        <AppButton variant="outline" startIcon={<WorkOutlinedIcon />} onClick={() => onGo('missions', 'import')}>Importer des missions</AppButton>
        <AppButton variant="secondary" startIcon={<CompareArrowsIcon />} onClick={() => onGo('matching', 'candidate-missions')}>Lancer un matching</AppButton>
      </Box>
    </Panel>
  </Box>
);

const readNav = () => {
  const params = new URLSearchParams(window.location.search);
  return { section: params.get('section'), tab: params.get('tab') };
};

const Sourcing = ({ snackbarShowMessage }) => {
  // Kept in local state (URL updated silently) so a running import is never lost by a route re-render.
  const [nav, setNav] = useState(readNav);
  const section = SECTIONS.find((s) => s.key === nav.section) || SECTIONS[0];
  const tab = section.tabs ? (section.tabs.find((t) => t.key === nav.tab) || section.tabs[0]) : null;

  const linkedin = useBrowserLinkedin();
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState('');
  const [statsLoading, setStatsLoading] = useState(true);

  const loadStats = useCallback(() => {
    setStatsLoading(true);
    fetchDashboard()
      .then((d) => { setStats(d); setStatsError(''); })
      .catch((e) => setStatsError(e.message))
      .finally(() => setStatsLoading(false));
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const candidateImport = useImportOperation(CANDIDATE_STEPS, loadStats);
  const missionImport = useImportOperation(MISSION_STEPS, loadStats);

  const go = (sectionKey, tabKey) => {
    setNav({ section: sectionKey, tab: tabKey || null });
    const sp = new URLSearchParams();
    sp.set('section', sectionKey);
    if (tabKey) sp.set('tab', tabKey);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${sp.toString()}`);
  };

  const tabBadge = (sectionKey, tabKey) => {
    if (sectionKey === 'candidates' && stats) {
      if (tabKey === 'stored') return stats.stored;
      if (tabKey === 'queue') return stats.ready_for_storage;
      if (tabKey === 'rejected') return (stats.not_storable || 0) + (stats.extraction_failed || 0);
    }
    if (tabKey === 'import') {
      const op = sectionKey === 'candidates' ? candidateImport.op : missionImport.op;
      if (op.phase === 'running') return '…';
    }
    return undefined;
  };

  const shared = { snackbarShowMessage, onChanged: loadStats };
  let content;
  if (section.key === 'dashboard') {
    content = <DashboardView stats={stats} error={statsError} loading={statsLoading} onRefresh={loadStats} onGo={go} />;
  } else if (section.key === 'candidates') {
    if (tab.key === 'import') content = <ImportCandidatesView op={candidateImport.op} onLaunch={candidateImport.start} onReset={candidateImport.reset} onGoTo={(t) => go('candidates', t)} />;
    else if (tab.key === 'stored') content = <StoredCandidatesView {...shared} />;
    else if (tab.key === 'queue') content = <QueueView {...shared} />;
    else content = <RejectionsView {...shared} />;
  } else if (section.key === 'missions') {
    if (tab.key === 'import') content = <ImportMissionsView op={missionImport.op} onLaunch={missionImport.start} onReset={missionImport.reset} onGoTo={(t) => go('missions', t)} />;
    else if (tab.key === 'stored') content = <MissionsView {...shared} />;
    else content = <MissionOperationsView key={tab.key} kind={tab.key === 'queue' ? 'failed' : 'rejected'} {...shared} />;
  } else {
    content = <MatchingView key={tab.key} direction={tab.key} snackbarShowMessage={snackbarShowMessage} />;
  }

  return (
    <LinkedinContext.Provider value={linkedin}>
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: '100%', minWidth: 0, bgcolor: '#f5f7fa' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', px: { xs: 2, md: 3 }, pt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <IconBox><TravelExploreIcon /></IconBox>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>Sourcing IA</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>Collecte LinkedIn de candidats et de missions, contrôle qualité et matching</Typography>
          </Box>
          <Box sx={{ order: { xs: 3, md: 0 }, width: { xs: '100%', md: 'auto' }, maxWidth: { md: 520 } }}>
            <LinkedinAccount compact snackbarShowMessage={snackbarShowMessage} />
          </Box>
          {[candidateImport.op, missionImport.op].some((o) => o.phase === 'running') && (
            <Chip
              size="small"
              icon={<CircularProgress size={12} sx={{ color: 'inherit' }} />}
              label="Import en cours"
              onClick={() => go(candidateImport.op.phase === 'running' ? 'candidates' : 'missions', 'import')}
              sx={{ fontWeight: 700, bgcolor: '#f0f7fd', color: '#03407b', border: '1px solid #cfe0f1', '& .MuiChip-icon': { color: '#03407b' } }}
            />
          )}
        </Box>

        <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, overflowX: 'auto' }}>
          {SECTIONS.map((s) => {
            const active = s.key === section.key;
            return (
              <Box
                key={s.key}
                role="tab"
                tabIndex={0}
                aria-selected={active}
                onClick={() => go(s.key, s.tabs?.[0]?.key)}
                onKeyDown={(e) => e.key === 'Enter' && go(s.key, s.tabs?.[0]?.key)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75, px: 1.75, py: 1.1, cursor: 'pointer', whiteSpace: 'nowrap',
                  fontSize: '0.86rem', fontWeight: 700, color: active ? '#03407b' : '#64748b',
                  borderBottom: `2.5px solid ${active ? '#03407b' : 'transparent'}`,
                  '& svg': { fontSize: 18 },
                  '&:hover': { color: '#03407b' },
                }}
              >
                {s.icon}{s.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 1.5, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {section.tabs && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {section.tabs.map((t) => {
              const active = t.key === tab.key;
              const badge = tabBadge(section.key, t.key);
              return (
                <Chip
                  key={t.key}
                  onClick={() => go(section.key, t.key)}
                  label={
                    <>
                      {t.label}
                      {badge !== undefined && (
                        <Box component="span" sx={{ ml: 0.75, px: 0.75, borderRadius: '6px', fontSize: '0.7rem', fontWeight: 800, bgcolor: active ? 'rgba(255,255,255,0.22)' : 'rgba(3,64,123,0.1)' }}>{badge}</Box>
                      )}
                    </>
                  }
                  sx={{
                    height: 32, fontSize: '0.8rem', fontWeight: 700, borderRadius: '9px', cursor: 'pointer',
                    bgcolor: active ? '#03407b' : '#fff', color: active ? '#fff' : '#475569', border: `1px solid ${active ? '#03407b' : '#e2e8f0'}`,
                    '&:hover': { bgcolor: active ? '#02357a' : '#f0f7fd' },
                  }}
                />
              );
            })}
          </Box>
        )}
        {content}
      </Box>
    </Box>
    </LinkedinContext.Provider>
  );
};

export default withSnackbar(Sourcing);
